import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Users, Edit, Trash2, User, UserCheck } from 'lucide-react';
import axios from 'axios';

import { useAuth, API } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';

const TablesTab = () => {
  const { getAuthHeaders } = useAuth();
  const [tables, setTables] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableInfo, setTableInfo] = useState({});
  const [formData, setFormData] = useState({
    number: 1,
    capacity: 4
  });

  useEffect(() => {
    loadTables();
    loadTableOrders();
  }, []);

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeaders()
      });
      // Ordenar mesas por número de menor a mayor
      const list = Array.isArray(response.data) ? [...response.data] : [];
      list.sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0));
      setTables(list);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cargar mesas');
    }
  };

  // Mapa mesa -> { cliente, mesero } construido con las ordenes abiertas. El backend
  // libera solo las mesas con orden entregada/cancelada, así que una mesa ocupada
  // siempre tiene su orden en pending/in_preparation/ready (mismo criterio que Meseros).
  const loadTableOrders = async () => {
    try {
      const response = await axios.get(
        `${API}/orders?status=pending&status=in_preparation&status=ready&limit=200`,
        { headers: getAuthHeaders() }
      );
      const ordersList = Array.isArray(response.data) ? response.data : [];
      const map = {};
      ordersList.forEach((order) => {
        if (order.order_type === 'dine_in' && order.table_id) {
          map[order.table_id] = {
            customerName: order.customer_name || '',
            waiterName: order.waiter_name || ''
          };
        }
      });
      setTableInfo(map);
    } catch {
      // No bloquear la gestión de mesas si no se pueden cargar las órdenes
    }
  };

  const handleCreateTable = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingTable) {
        // Update existing table
        await axios.put(`${API}/tables/${editingTable.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Mesa actualizada exitosamente');
      } else {
        // Create new table
        await axios.post(`${API}/tables`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Mesa creada exitosamente');
      }
      
      await loadTables();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar mesa');
    }

    setLoading(false);
  };

  const handleEditTable = (table) => {
    setEditingTable(table);
    setFormData({
      number: table.number,
      capacity: table.capacity
    });
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingTable(null);
      setFormData({ number: tables.length + 1, capacity: 4 });
    }, 100);
  };

  const handleOpenDialog = () => {
    setEditingTable(null);
    setFormData({ number: tables.length + 1, capacity: 4 });
    setIsDialogOpen(true);
  };

  const handleDeleteTable = async (tableId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta mesa?')) {
      return;
    }

    try {
      await axios.delete(`${API}/tables/${tableId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Mesa eliminada exitosamente');
      loadTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar mesa');
    }
  };

  const getTableStatusClass = (status) => {
    switch (status) {
      case 'free':
        return 'table-free border-2';
      case 'occupied':
        return 'table-occupied border-2';
      case 'reserved':
        return 'table-reserved border-2';
      default:
        return '';
    }
  };

  const getTableStatusText = (status) => {
    switch (status) {
      case 'free':
        return 'Libre';
      case 'occupied':
        return 'Ocupada';
      case 'reserved':
        return 'Reservada';
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Gestión de Mesas</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={handleOpenDialog}
              data-testid="create-table-button" 
              className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Mesa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTable ? 'Editar Mesa' : 'Crear Nueva Mesa'}</DialogTitle>
              <DialogDescription>
                {editingTable ? 'Modifica los datos de la mesa' : 'Ingresa los datos para crear una nueva mesa'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateTable} className="space-y-4">
              <div>
                <Label>Número de Mesa</Label>
                <Input
                  type="number"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: parseInt(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Capacidad</Label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  required
                  className="rounded-xl"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                {loading ? 'Guardando...' : (editingTable ? 'Actualizar Mesa' : 'Crear Mesa')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {tables.map((table) => (
          <Card
            key={table.id}
            data-testid={`table-card-${table.id}`}
            className={`p-6 ${getTableStatusClass(table.status)} bg-white/80 backdrop-blur-xl hover-lift`}
          >
            <Users className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <h3 className="text-xl font-bold text-gray-800 mb-1">Mesa {table.number}</h3>
            <p className="text-sm text-gray-600 mb-2">Cap: {table.capacity}</p>
            <p className={`text-xs font-semibold ${table.status === 'occupied' ? 'mb-2' : 'mb-3'}`}>{getTableStatusText(table.status)}</p>
            {table.status === 'occupied' && (
              <div className="mb-3 space-y-1">
                <p
                  className="text-xs text-gray-700 truncate"
                  title={tableInfo[table.id]?.customerName || 'Cliente sin nombre'}
                >
                  <User className="w-3 h-3 inline-block mr-1 text-orange-500 -mt-0.5" />
                  {tableInfo[table.id]?.customerName || 'Cliente sin nombre'}
                </p>
                <p
                  className="text-xs text-gray-500 truncate"
                  title={tableInfo[table.id]?.waiterName || 'Sin mesero asignado'}
                >
                  <UserCheck className="w-3 h-3 inline-block mr-1 text-blue-500 -mt-0.5" />
                  {tableInfo[table.id]?.waiterName || 'Sin mesero'}
                </p>
              </div>
            )}
            
            <div className="flex gap-1">
              <Button
                onClick={() => handleEditTable(table)}
                variant="outline"
                size="sm"
                data-testid={`edit-table-${table.id}`}
                className="flex-1 border-orange-200 hover:bg-orange-50 rounded-lg text-xs"
              >
                <Edit className="w-3 h-3" />
              </Button>
              <Button
                onClick={() => handleDeleteTable(table.id)}
                variant="outline"
                size="sm"
                data-testid={`delete-table-${table.id}`}
                className="flex-1 border-red-200 hover:bg-red-50 text-red-600 rounded-lg text-xs"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {tables.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No hay mesas configuradas</p>
          <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Crear Primera Mesa
          </Button>
        </div>
      )}
    </div>
  );
};

export default TablesTab;
